import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Button, CameraIcon, Card, Select, Text } from '@hivewatch/dark'
import type { SelectOption } from '@hivewatch/dark'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectPlacedDeviceIds,
  setCameraAtCell,
} from '@/redux/videoWall/videoWallSlice'
import { trackEvent } from '@/utils/analytics'

import { VIDEO_WALL_ANALYTICS } from '../constants/constants'
import { CloudCameraOptionIProps } from '../types/types'

interface IProps {
  index: number
  availableCameras: CloudCameraOptionIProps[]
  camerasLoading: boolean
  hasCamerasError: boolean
  onRetryCameras: () => void
}

const buildLabel = (camera: CloudCameraOptionIProps): string =>
  camera.facilityName ? `${camera.name} - ${camera.facilityName}` : camera.name

/**
 * An empty grid slot: a prompt plus an inline dropdown of cloud cameras not yet
 * placed on the wall. Selecting one fills this cell.
 */
export const EmptyCell = ({
  index,
  availableCameras,
  camerasLoading,
  hasCamerasError,
  onRetryCameras,
}: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const placedDeviceIds = useAppSelector(selectPlacedDeviceIds)

  const options: SelectOption[] = useMemo(
    () =>
      availableCameras
        .filter((camera) => !placedDeviceIds.includes(camera.deviceId))
        .map((camera) => ({
          value: camera.deviceId,
          label: buildLabel(camera),
        })),
    [availableCameras, placedDeviceIds]
  )

  const handleChange = (option: SelectOption | null) => {
    if (!option) {
      return
    }
    const camera = availableCameras.find((c) => c.deviceId === option.value)
    if (!camera) {
      return
    }
    dispatch(setCameraAtCell({ index, camera }))
    trackEvent(VIDEO_WALL_ANALYTICS.cameraAdded, {
      camera_id: camera.deviceId,
      streaming_type: camera.streamingType,
      cell_index: index,
      camera_count: placedDeviceIds.length + 1,
    })
  }

  const renderBody = () => {
    if (!camerasLoading && hasCamerasError) {
      return (
        <>
          <Text color='text.secondary' fontSize='14px' textAlign='center'>
            {t('video_wall:cameras_error')}
          </Text>
          <Button onClick={onRetryCameras} size='sm' variant='secondary'>
            {t('video_wall:retry')}
          </Button>
        </>
      )
    }

    if (!camerasLoading && options.length === 0) {
      return (
        <Text color='text.secondary' fontSize='14px' textAlign='center'>
          {availableCameras.length === 0
            ? t('video_wall:no_cloud_cameras')
            : t('video_wall:all_cameras_placed')}
        </Text>
      )
    }

    return (
      <>
        <Text color='text.secondary' fontSize='14px'>
          {t('video_wall:add_camera')}
        </Text>
        <Box maxW='280px' w='100%'>
          <Select
            isLoading={camerasLoading}
            onChange={handleChange}
            options={options}
            placeholder={t('video_wall:select_camera_placeholder')}
            value={null}
          />
        </Box>
      </>
    )
  }

  return (
    <Card
      alignItems='center'
      bg='bg.card'
      data-testid='video-wall-empty-cell'
      display='flex'
      flexDirection='column'
      gap='16px'
      h='100%'
      justifyContent='center'
      p='24px'
    >
      <CameraIcon size={32} />
      {renderBody()}
    </Card>
  )
}
